import { type FormEvent, useEffect, useState } from "react";

import type { RecipeSuggestion } from "@sytofood/shared";

import {
  type DailySummaryView,
  type LoggedMeal,
  type ParseMealView,
  type TargetsView,
  api,
} from "../lib/api";
import { todayIso } from "../lib/day";
import { useLang } from "../lib/i18n";
import { pantryCopy } from "../lib/i18n/pantry";
import { normalizeToPantryUnit } from "../lib/unit-normalize";
import { ScreenShell } from "./screen-shell";
import { AiFooter, Eyebrow, H1, MonoMeta, Stat, TopBar, confLevelFromScore } from "./ui/primitives";

type Props = {
  householdId: string;
  targets: TargetsView;
  onLogged: (meal: LoggedMeal) => void;
  onUseAsMeal: (parsed: ParseMealView) => void;
  onBack: () => void;
};


function remainingOf(
  targets: TargetsView,
  totals: DailySummaryView["totals"],
): { kcal: number | null; proteinG: number | null; fatG: number | null; carbsG: number | null } {
  return {
    kcal: targets.kcalTarget !== null ? Math.max(0, targets.kcalTarget - totals.kcal) : null,
    proteinG:
      targets.proteinTargetG !== null
        ? Math.max(0, targets.proteinTargetG - totals.proteinG)
        : null,
    fatG: targets.fatTargetG !== null ? Math.max(0, targets.fatTargetG - totals.fatG) : null,
    carbsG:
      targets.carbsTargetG !== null ? Math.max(0, targets.carbsTargetG - totals.carbsG) : null,
  };
}

export function CravingScreen({ householdId, targets, onLogged, onUseAsMeal, onBack }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const [summary, setSummary] = useState<DailySummaryView | null>(null);
  const [craving, setCraving] = useState("");
  const [pantryOnly, setPantryOnly] = useState(false);
  const [recipes, setRecipes] = useState<RecipeSuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loggingIdx, setLoggingIdx] = useState<number | null>(null);
  const [addingShopIdx, setAddingShopIdx] = useState<number | null>(null);
  const [addedShopIdx, setAddedShopIdx] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    api
      .getDailySummary(householdId, todayIso())
      .then((res) => {
        if (!cancelled) setSummary(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t.errLoad);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  const remaining = summary
    ? remainingOf(targets, summary.totals)
    : { kcal: null, proteinG: null, fatG: null, carbsG: null };

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!craving.trim()) return;
    setLoading(true);
    setError(null);
    setRecipes(null);
    setAddedShopIdx(new Set());
    try {
      const res = await api.suggestForCraving(householdId, {
        craving: craving.trim(),
        pantryOnly,
        hourLocal: new Date().getHours(),
      });
      setRecipes(res.recipes);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.cravingErrGenerate);
    } finally {
      setLoading(false);
    }
  }

  async function quickLog(recipe: RecipeSuggestion, idx: number) {
    setLoggingIdx(idx);
    setError(null);
    try {
      const { meals } = await api.saveMeals(
        {
          items: [
            {
              name: recipe.name,
              kcal: recipe.estimatedKcal,
              proteinG: recipe.estimatedProteinG,
              fatG: recipe.estimatedFatG,
              carbsG: recipe.estimatedCarbsG,
              portionGrams: null,
              confidence: recipe.confidence,
            },
          ],
          source: "manual_ai",
          ...(recipe.mealType ? { mealType: recipe.mealType } : {}),
        },
        householdId,
      );
      const meal = meals[0];
      if (meal) onLogged(meal);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errLog);
    } finally {
      setLoggingIdx(null);
    }
  }

  function editBeforeLog(recipe: RecipeSuggestion) {
    onUseAsMeal({
      items: [
        {
          name: recipe.name,
          kcal: recipe.estimatedKcal,
          proteinG: recipe.estimatedProteinG,
          fatG: recipe.estimatedFatG,
          carbsG: recipe.estimatedCarbsG,
          portionGrams: null,
          confidence: recipe.confidence,
        },
      ],
      source: "manual_ai",
      rawInputText: `Pomysł: ${recipe.name}`,
    });
  }

  async function addMissingToShopping(recipe: RecipeSuggestion, idx: number) {
    if (recipe.missing.length === 0) return;
    setAddingShopIdx(idx);
    setError(null);
    try {
      await api.bulkCreateShopping(householdId, {
        items: recipe.missing.map((ing) => ({
          name: ing.name,
          quantity: ing.quantity,
          unit: normalizeToPantryUnit(ing.unit),
          source: "recipe",
        })),
      });
      setAddedShopIdx((prev) => new Set(prev).add(idx));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errAddToList);
    } finally {
      setAddingShopIdx(null);
    }
  }

  const budgetLine = (() => {
    const parts: string[] = [];
    if (remaining.kcal !== null) parts.push(`${remaining.kcal} KCAL`);
    if (remaining.proteinG !== null) parts.push(`B${Math.round(remaining.proteinG)}`);
    if (remaining.fatG !== null) parts.push(`T${Math.round(remaining.fatG)}`);
    if (remaining.carbsG !== null) parts.push(`W${Math.round(remaining.carbsG)}`);
    return parts.length > 0
      ? `${t.cravingBudgetPrefix}${parts.join(" · ")}`
      : t.cravingBudgetEmpty;
  })();

  return (
    <ScreenShell hideHeader withBottomNav>
      <div style={{ marginBottom: 18 }}>
        <TopBar back={t.cravingBack} title={t.cravingTopTitle} onBack={onBack} />
      </div>

      <H1 accent="…">{t.cravingTitle}</H1>

      {/* Prompt + budget card */}
      <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
        <div
          style={{
            padding: "14px 16px",
            borderRadius: 12,
            background: "var(--surface)",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
        >
          <input
            type="text"
            required
            maxLength={200}
            value={craving}
            onChange={(e) => setCraving(e.target.value)}
            placeholder={t.cravingPlaceholder}
            className="w-full outline-none bg-transparent"
            style={{
              // 16px — see primitives.tsx Input comment: anything below
              // triggers the iOS auto-zoom-on-focus bug that never
              // recovers without a manual pinch.
              fontSize: 16,
              fontWeight: 500,
              color: "var(--ink)",
            }}
          />
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px solid var(--nav-border)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <MonoMeta>{budgetLine}</MonoMeta>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--accent)",
                letterSpacing: "0.06em",
                fontWeight: 700,
              }}
            >
              SYTO SENSE
            </span>
          </div>
        </div>

        {/* Pantry-only toggle */}
        <button
          type="button"
          onClick={() => setPantryOnly((v) => !v)}
          style={{
            marginTop: 14,
            padding: "10px 14px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--surface-2)",
            borderRadius: 10,
            border: "none",
            textAlign: "left",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)" }}>
            {t.cravingPantryToggle}
          </span>
          <span
            style={{
              width: 34,
              height: 20,
              borderRadius: 99,
              background: pantryOnly ? "var(--accent)" : "var(--track)",
              position: "relative",
              display: "inline-block",
              transition: "background 150ms",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: pantryOnly ? "auto" : 2,
                right: pantryOnly ? 2 : "auto",
                top: 2,
                width: 16,
                height: 16,
                borderRadius: 99,
                background: "#fff",
                transition: "left 150ms, right 150ms",
              }}
            />
          </span>
        </button>

        <button
          type="submit"
          disabled={loading || !craving.trim()}
          className="w-full inline-flex items-center justify-center gap-2 uppercase disabled:opacity-50"
          style={{
            marginTop: 14,
            padding: "14px 16px",
            borderRadius: 12,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            border: "none",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            boxShadow: "var(--shadow-cta)",
          }}
        >
          {loading ? t.cravingThinking : t.cravingSubmit}
        </button>
      </form>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 mt-3 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
        >
          {error}
        </p>
      ) : null}

      {recipes && recipes.length === 0 ? (
        <p
          className="text-center px-4"
          style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}
        >
          {t.cravingEmpty}
        </p>
      ) : null}

      {recipes && recipes.length > 0 ? (
        <div style={{ marginTop: 24 }}>
          {recipes.map((recipe, idx) => {
            const conf = confLevelFromScore(recipe.confidence);
            return (
              <section
                key={`${recipe.name}-${idx}`}
                style={{
                  paddingBottom: 18,
                  marginBottom: 18,
                  borderBottom: idx < recipes.length - 1 ? "1px solid var(--nav-border)" : "none",
                }}
              >
                <Eyebrow>
                  {String(idx + 1).padStart(2, "0")} ·{" "}
                  {idx === 0 ? t.cravingBestMatch : t.cravingAlternative}
                </Eyebrow>
                <h2
                  className="tabular"
                  style={{
                    marginTop: 10,
                    fontFamily: "var(--font-display)",
                    fontSize: 26,
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.15,
                    color: "var(--ink)",
                  }}
                >
                  {recipe.name}
                </h2>

                {/* Stats grid */}
                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    gap: 24,
                    paddingBottom: 14,
                    borderBottom: "1px solid var(--nav-border)",
                    flexWrap: "wrap",
                  }}
                >
                  <Stat label={t.cravingStatKcal} value={recipe.estimatedKcal} />
                  {recipe.estimatedProteinG !== null ? (
                    <Stat
                      label={t.cravingStatProtein}
                      value={`${Math.round(recipe.estimatedProteinG)}g`}
                    />
                  ) : null}
                  {recipe.estimatedFatG !== null ? (
                    <Stat
                      label={t.cravingStatFat}
                      value={`${Math.round(recipe.estimatedFatG)}g`}
                    />
                  ) : null}
                  {recipe.estimatedCarbsG !== null ? (
                    <Stat
                      label={t.cravingStatCarbs}
                      value={`${Math.round(recipe.estimatedCarbsG)}g`}
                    />
                  ) : null}
                  <span style={{ marginLeft: "auto" }}>
                    <span
                      className="text-[11px] font-semibold tracking-[0.02em]"
                      style={{
                        padding: "3px 8px",
                        borderRadius: 6,
                        background:
                          conf === "high"
                            ? "var(--ok-bg)"
                            : conf === "mid"
                              ? "var(--warn-bg)"
                              : "var(--bad-bg)",
                        color:
                          conf === "high"
                            ? "var(--ok-fg)"
                            : conf === "mid"
                              ? "var(--warn-fg)"
                              : "var(--bad-fg)",
                      }}
                    >
                      {conf === "high"
                        ? t.cravingConfHigh
                        : conf === "mid"
                          ? t.cravingConfMid
                          : t.cravingConfLow}
                    </span>
                  </span>
                </div>

                {/* Have / missing rows */}
                <div
                  style={{
                    padding: "14px 0",
                    borderBottom: "1px solid var(--nav-border)",
                  }}
                >
                  {recipe.uses.length > 0 ? (
                    <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--ok-fg)",
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          flex: "0 0 60px",
                        }}
                      >
                        {t.cravingHaveLabel}
                      </div>
                      <div style={{ fontSize: 13, color: "var(--ink-2)", flex: 1 }}>
                        {recipe.uses
                          .map((u) =>
                            u.quantity !== null
                              ? `${u.name} (${u.quantity}${u.unit ? ` ${u.unit}` : ""})`
                              : u.name,
                          )
                          .join(" · ")}
                      </div>
                    </div>
                  ) : null}
                  {recipe.missing.length > 0 ? (
                    <div style={{ display: "flex", gap: 10 }}>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--bad-fg)",
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          flex: "0 0 60px",
                        }}
                      >
                        {t.cravingMissingLabel}
                      </div>
                      <div style={{ fontSize: 13, color: "var(--ink-2)", flex: 1 }}>
                        {recipe.missing
                          .map((m) =>
                            m.quantity !== null
                              ? `${m.name} (${m.quantity}${m.unit ? ` ${m.unit}` : ""})`
                              : m.name,
                          )
                          .join(" · ")}{" "}
                        <button
                          type="button"
                          onClick={() => addMissingToShopping(recipe, idx)}
                          disabled={addingShopIdx === idx || addedShopIdx.has(idx)}
                          className="disabled:opacity-60"
                          style={{
                            color: "var(--accent)",
                            fontWeight: 700,
                            fontSize: 13,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          {addedShopIdx.has(idx)
                            ? t.cravingAddedToList
                            : addingShopIdx === idx
                              ? t.cravingAddingToList
                              : t.cravingAddToList}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Step list */}
                <div style={{ marginTop: 14 }}>
                  <Eyebrow>{t.cravingStepByStep}</Eyebrow>
                  <p
                    style={{
                      marginTop: 8,
                      fontSize: 13,
                      color: "var(--ink-2)",
                      lineHeight: 1.6,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {recipe.instructions}
                  </p>
                </div>

                {/* Actions */}
                <div style={{ marginTop: 18, display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => quickLog(recipe, idx)}
                    disabled={loggingIdx === idx}
                    className="uppercase disabled:opacity-50"
                    style={{
                      flex: 1,
                      padding: "14px 16px",
                      borderRadius: 10,
                      background: "var(--ink)",
                      color: "var(--bg)",
                      border: "none",
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                    }}
                  >
                    {loggingIdx === idx ? t.saving : t.cravingLogIt}
                  </button>
                  <button
                    type="button"
                    onClick={() => editBeforeLog(recipe)}
                    className="uppercase"
                    style={{
                      padding: "14px 16px",
                      borderRadius: 10,
                      background: "transparent",
                      color: "var(--ink)",
                      boxShadow: "inset 0 0 0 1px var(--nav-border)",
                      border: "none",
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                    }}
                  >
                    {t.cravingEdit}
                  </button>
                </div>
              </section>
            );
          })}
        </div>
      ) : null}

      <AiFooter />
    </ScreenShell>
  );
}
